import { Capacitor } from '@capacitor/core';
import { AudioConfig, VoiceProvider, ProviderInfo } from './types';

/**
 * Output audio configuration for realtime voice
 * Format: PCM16, 24kHz, Mono
 */
export const outputAudioConfig: AudioConfig = {
  chunk: 3200,
  format: "pcm",
  channels: 1,
  sampleRate: 24000,
  bitSize: 16, // 16-bit PCM
};

/**
 * 可用的 Provider 配置
 */
export const PROVIDERS: Record<VoiceProvider, ProviderInfo> = {
  huoshan: {
    name: 'huoshan',
    displayName: '火山引擎',
    description: '中文优化，低延迟',
    supportedLanguages: ['zh', 'zh-CN'],
    defaultLanguage: 'zh'
  },
  azure: {
    name: 'azure',
    displayName: 'Azure VoiceLive',
    description: '多语言支持，GPT-4o',
    supportedLanguages: ['en', 'zh', 'ja', 'ko', 'es', 'fr', 'de'],
    defaultLanguage: 'en'
  },
  azure_pipeline: {
    name: 'azure_pipeline',
    displayName: 'Azure Pipeline (Cost-Effective)',
    description: '本地VAD + Azure STT/TTS',
    supportedLanguages: ['en', 'zh', 'es'],
    defaultLanguage: 'en'
  }
};

/**
 * 语种定义
 */
export type SupportedLanguage = 'zh' | 'en' | 'es';

export interface LanguageInfo {
  code: SupportedLanguage;
  name: string;
  provider: VoiceProvider;
}

/**
 * 支持的语种列表及其对应的 Provider
 * - 中文 (zh) -> 火山引擎
 * - 英文 (en) -> Azure Pipeline
 * - 西班牙语 (es) -> Azure Pipeline
 */
export const LANGUAGES: Record<SupportedLanguage, LanguageInfo> = {
  zh: {
    code: 'zh',
    name: '中文',
    provider: 'huoshan'
  },
  en: {
    code: 'en',
    name: 'English(US)',
    provider: 'azure_pipeline'
  },
  es: {
    code: 'es',
    name: 'Español',
    provider: 'azure_pipeline'
  }
};

/**
 * 根据语种获取推荐的 Provider
 */
export function getProviderByLanguage(lang: string): VoiceProvider {
  // 简单的前缀匹配
  if (lang.startsWith('zh')) return 'huoshan';

  // 对于非中文，优先使用 azure_pipeline (更省钱)
  // 除非用户显式指定 azure (在 UI 上选择)
  if (lang.startsWith('en')) return 'azure_pipeline';
  if (lang.startsWith('es')) return 'azure_pipeline';

  // 默认回退到 Azure Pipeline
  return 'azure_pipeline';
}

/**
 * 获取实时语音服务器地址
 * 
 * 优先级：
 * 1. 环境变量 VITE_REALTIME_VOICE_SERVER_URL (推荐) 或 VITE_HUOSHAN_SERVER_URL (兼容旧版)
 * 2. 根据平台自动选择：
 *    - Web: ws://localhost:8000/ws/control
 *    - iOS/Android: ws://3.141.98.46:8000/ws/control (EC2 公网 IP)
 * 
 * 配置说明：
 * - 在项目根目录创建 .env 文件，添加：
 *   VITE_REALTIME_VOICE_SERVER_URL=ws://你的电脑IP:8000/ws/control
 * - 查找电脑 IP：macOS/Linux 运行 `ifconfig | grep "inet "`，Windows 运行 `ipconfig`
 * - 确保手机和电脑在同一 WiFi 网络下
 * 
 * 注意：此服务器支持多个 provider（Huoshan 和 Azure），会根据客户端请求自动路由
 */
export function getRealtimeVoiceServerUrl(): string {
  // 优先使用新的环境变量名
  const newEnvUrl = (import.meta as any).env?.VITE_REALTIME_VOICE_SERVER_URL;
  if (newEnvUrl) {
    console.log('[RealtimeVoice] 使用环境变量配置的服务器地址:', newEnvUrl);
    return newEnvUrl;
  }

  // 向后兼容：支持旧的环境变量名
  const legacyEnvUrl = (import.meta as any).env?.VITE_HUOSHAN_SERVER_URL;
  if (legacyEnvUrl) {
    console.log('[RealtimeVoice] 使用环境变量配置的服务器地址 (legacy):', legacyEnvUrl);
    return legacyEnvUrl;
  }

  // 根据平台选择默认地址
  const platform = Capacitor.getPlatform();

  if (platform === 'web') {
    // Web 开发环境使用 localhost
    const url = 'ws://localhost:8000/ws/control';
    console.log('[RealtimeVoice] Web 平台，使用默认地址:', url);
    return url;
  } else {
    // iOS/Android 使用 EC2 公网 IP
    // 默认使用 EC2 公网 IP，用户可以通过环境变量覆盖
    // 如果 IP 不对，请在 .env 文件中配置 VITE_REALTIME_VOICE_SERVER_URL
    const url = 'ws://54.145.174.9.sslip.io/ws/control';
    console.log('[RealtimeVoice] 移动平台，使用默认地址:', url);
    console.log('[RealtimeVoice] 提示：如果连接失败，请在 .env 文件中配置 VITE_REALTIME_VOICE_SERVER_URL');
    return url;
  }
}

/**
 * Dialog control configuration used by the realtime engine to determine
 * when to proactively ping the model (inactivity prompt) and when to
 * auto-stop a stalled session.
 */
export const dialogControlConfig = {
  /**
   * After this many milliseconds without user activity we will trigger
   * a lightweight text query asking whether the user would like to continue.
   */
  inactivityPromptMs: 10000,
  /**
   * If the user still doesn't respond after the inactivity prompt,
   * automatically end the session to free resources.
   */
  noResponseAutoStopMs: 10000,
} as const;